import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Book, Sparkles, Hash, ChevronDown, ChevronUp, Calculator, Calendar, Star, Clock, AlertTriangle, Heart, Compass, Users, Brain, Zap, Crown, Lock, ArrowRight, Loader2 } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Significations des nombres 1-9
const NUMBER_MEANINGS = [
  { number: 1, name: "Le Leader", keywords: "Indépendance, initiative, ambition", traits: "Pionnier, autonome, confiant", challenges: "Égocentrisme, impatience", color: "bg-red-500" },
  { number: 2, name: "Le Diplomate", keywords: "Coopération, sensibilité, équilibre", traits: "Médiateur, intuitif, patient", challenges: "Indécision, dépendance", color: "bg-orange-500" },
  { number: 3, name: "Le Créatif", keywords: "Expression, communication, joie", traits: "Artiste, optimiste, sociable", challenges: "Dispersion, superficialité", color: "bg-yellow-500" },
  { number: 4, name: "Le Bâtisseur", keywords: "Stabilité, travail, organisation", traits: "Méthodique, loyal, pratique", challenges: "Rigidité, obstination", color: "bg-green-500" },
  { number: 5, name: "L'Aventurier", keywords: "Liberté, changement, adaptabilité", traits: "Curieux, dynamique, versatile", challenges: "Instabilité, excès", color: "bg-teal-500" },
  { number: 6, name: "Le Responsable", keywords: "Harmonie, famille, service", traits: "Protecteur, aimant, équilibré", challenges: "Perfectionnisme, sacrifice", color: "bg-blue-500" },
  { number: 7, name: "Le Mystique", keywords: "Spiritualité, analyse, sagesse", traits: "Introspectif, chercheur, intuitif", challenges: "Isolement, pessimisme", color: "bg-indigo-500" },
  { number: 8, name: "Le Puissant", keywords: "Pouvoir, succès, abondance", traits: "Ambitieux, efficace, visionnaire", challenges: "Matérialisme, domination", color: "bg-purple-500" },
  { number: 9, name: "L'Humaniste", keywords: "Compassion, sagesse, accomplissement", traits: "Altruiste, idéaliste, universel", challenges: "Idéalisme excessif, détachement", color: "bg-pink-500" }
];

// Nombres maîtres
const MASTER_NUMBERS = [
  { number: 11, name: "Le Visionnaire", description: "Intuition puissante, inspiration spirituelle, leadership éclairé. Le 11 porte une vibration élevée combinant le 1 (initiative) doublé et réduit au 2 (diplomatie).", traits: ["Intuition exceptionnelle", "Capacité d'inspiration", "Sensibilité accrue", "Potentiel de guérison"] },
  { number: 22, name: "Le Maître Bâtisseur", description: "Capacité de réaliser de grands projets, vision pratique, construction durable. Le 22 combine la vision du 11 avec la capacité de construction du 4.", traits: ["Vision à grande échelle", "Capacité de concrétisation", "Leadership pratique", "Héritage durable"] },
  { number: 33, name: "Le Maître Enseignant", description: "Service désintéressé, guidance spirituelle, amour inconditionnel. Le 33 est le plus rare et représente l'accomplissement spirituel suprême.", traits: ["Compassion universelle", "Capacité d'enseignement", "Sacrifice bienveillant", "Guidance spirituelle"] }
];

// Dettes karmiques
const KARMIC_DEBTS = [
  { number: 13, lesson: "Travail et effort", description: "Leçon liée à la paresse ou aux raccourcis dans une vie passée. Demande d'apprendre la valeur du travail honnête et de la persévérance.", advice: "Cultivez la discipline et acceptez que le succès demande des efforts constants." },
  { number: 14, lesson: "Liberté et modération", description: "Abus de liberté ou d'excès sensoriels dans une vie passée. Invite à trouver l'équilibre entre liberté personnelle et responsabilité.", advice: "Pratiquez la modération et utilisez votre liberté de manière constructive." },
  { number: 16, lesson: "Ego et humilité", description: "Orgueil démesuré ou destruction des autres par égoïsme. Demande de reconstruire l'ego sur des bases d'humilité et d'authenticité.", advice: "Développez l'humilité et construisez des relations basées sur le respect mutuel." },
  { number: 19, lesson: "Indépendance et interdépendance", description: "Abus de pouvoir ou refus d'aider les autres. Invite à utiliser l'indépendance pour servir, pas pour dominer.", advice: "Utilisez votre force pour aider les autres et reconnaissez l'importance de la communauté." }
];

// Années personnelles
const PERSONAL_YEARS = [
  { year: 1, theme: "Nouveaux départs", description: "Début d'un cycle de 9 ans. Idéal pour initier des projets, prendre des initiatives et planter des graines.", action: "Osez, innovez, commencez !" },
  { year: 2, theme: "Patience & Coopération", description: "Année de développement des relations et de patience. Les graines plantées en année 1 ont besoin de temps.", action: "Cultivez les partenariats, soyez patient." },
  { year: 3, theme: "Expression & Créativité", description: "Année favorable à la communication, aux projets créatifs et à l'expansion sociale.", action: "Exprimez-vous, créez, connectez !" },
  { year: 4, theme: "Fondations & Travail", description: "Année de construction solide. Travail acharné pour établir des bases durables.", action: "Construisez, organisez, persévérez." },
  { year: 5, theme: "Changement & Liberté", description: "Année de transformation et de mouvement. Opportunités de changement et d'aventure.", action: "Adaptez-vous, explorez, évoluez !" },
  { year: 6, theme: "Responsabilité & Famille", description: "Focus sur la famille, le foyer et les responsabilités. Année d'harmonie domestique.", action: "Prenez soin des vôtres, équilibrez." },
  { year: 7, theme: "Introspection & Spiritualité", description: "Année de réflexion profonde, d'étude et de développement intérieur.", action: "Méditez, apprenez, explorez votre âme." },
  { year: 8, theme: "Récolte & Pouvoir", description: "Année de récompenses matérielles et de reconnaissance. Les efforts passés portent leurs fruits.", action: "Récoltez, gérez, investissez !" },
  { year: 9, theme: "Achèvement & Lâcher-prise", description: "Fin du cycle. Bilan, conclusion des projets et préparation au nouveau cycle.", action: "Terminez, faites le bilan, libérez." }
];

const ModuleCard = ({ title, icon: Icon, color, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="bg-white rounded-3xl border border-emerald-200 shadow-lg overflow-hidden"
    >
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full p-6 flex items-center justify-between ${color} transition-colors`}
      >
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
            <Icon className="h-6 w-6 text-white" />
          </div>
          <h2 className="text-xl md:text-2xl text-white text-left font-bold">{title}</h2>
        </div>
        {isOpen ? (
          <ChevronUp className="h-6 w-6 text-white" />
        ) : (
          <ChevronDown className="h-6 w-6 text-white" />
        )}
      </button>
      
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          exit={{ opacity: 0, height: 0 }}
          className="p-6"
        >
          {children}
        </motion.div>
      )}
    </motion.div>
  );
};

const NumerologiaKnowledgePage = () => {
  const navigate = useNavigate();
  const [hasPremiumAccess, setHasPremiumAccess] = useState(false);
  const [checkingAccess, setCheckingAccess] = useState(true);

  useEffect(() => {
    const checkAccess = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/premium/check/numerologia`, {
          withCredentials: true
        });
        setHasPremiumAccess(response.data.has_access === true);
      } catch (error) {
        setHasPremiumAccess(false);
      } finally {
        setCheckingAccess(false);
      }
    };
    checkAccess();
  }, []);

  // Loading state
  if (checkingAccess) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-emerald-50 via-white to-teal-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  // Premium upsell
  if (!hasPremiumAccess) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-emerald-50 via-white to-teal-50 py-12 px-4">
        <div className="max-w-5xl mx-auto">
          <Button
            variant="ghost"
            onClick={() => navigate('/numerologia')}
            className="mb-6 rounded-full text-gray-600 hover:bg-emerald-50"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Retour
          </Button>
          
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-100 rounded-full border border-emerald-200 mb-4">
              <Lock className="h-4 w-4 text-emerald-600" />
              <span className="text-emerald-700 text-sm font-medium">Premium</span>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Découvrir la Numérologie</h1>
            <p className="text-gray-600">Accédez à la base de connaissances complète</p>
          </div>
          
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="max-w-md mx-auto"
          >
            <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-3xl border-2 border-emerald-200 p-8 shadow-xl">
              <div className="flex justify-center mb-6">
                <div className="w-20 h-20 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full flex items-center justify-center shadow-lg">
                  <Crown className="h-10 w-10 text-white" />
                </div>
              </div>
              
              <div className="space-y-3 mb-8">
                {[
                  "Signification des 9 nombres",
                  "Nombres maîtres (11, 22, 33)",
                  "Dettes karmiques expliquées",
                  "Cycles et années personnelles",
                  "Guide de calcul complet"
                ].map((benefit, i) => (
                  <div key={i} className="flex items-center gap-3 p-2 bg-white/60 rounded-lg">
                    <Sparkles className="h-4 w-4 text-emerald-500 flex-shrink-0" />
                    <span className="text-sm text-gray-700">{benefit}</span>
                  </div>
                ))}
              </div>
              
              <Button
                onClick={() => navigate('/premium')}
                className="w-full bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white px-8 py-3 rounded-xl font-semibold shadow-lg"
                data-testid="upgrade-premium-btn"
              >
                <Crown className="h-5 w-5 mr-2" />
                Devenir Premium
                <ArrowRight className="h-5 w-5 ml-2" />
              </Button>
              
              <p className="text-xs text-gray-500 mt-4 text-center">
                À partir de 1.99€ pour 7 jours d'essai
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 via-white to-teal-50 py-12 px-4 md:px-8">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <Button
            data-testid="back-button"
            variant="ghost"
            onClick={() => navigate('/numerologia')}
            className="mb-6 rounded-full text-gray-600 hover:bg-emerald-50"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Retour
          </Button>
          
          <div className="bg-gradient-to-br from-emerald-500 to-teal-600 p-8 md:p-10 rounded-3xl shadow-xl mb-8">
            <div className="flex flex-col md:flex-row items-start gap-6">
              <div className="w-20 h-20 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center flex-shrink-0">
                <Book className="h-10 w-10 text-white" />
              </div>
              <div>
                <h1 className="text-3xl md:text-4xl lg:text-5xl mb-3 text-white font-bold">
                  Découvrir la Numérologie
                </h1>
                <p className="text-white/90 text-base md:text-lg mb-4">
                  L'art millénaire des nombres et de leurs vibrations
                </p>
                <p className="text-white/80 text-sm max-w-2xl">
                  La numérologie est un système de connaissance de soi basé sur la symbolique des nombres. 
                  Elle révèle des tendances et potentiels, pas des certitudes absolues.
                </p>
              </div>
            </div>
          </div>

          {/* Quick Navigation */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
            {[
              { icon: Hash, label: "Les Nombres", color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
              { icon: Star, label: "Maîtres", color: "bg-amber-50 text-amber-700 border-amber-200" },
              { icon: Calendar, label: "Cycles", color: "bg-teal-50 text-teal-700 border-teal-200" },
              { icon: Zap, label: "Karma", color: "bg-rose-50 text-rose-700 border-rose-200" }
            ].map((item, i) => (
              <div key={i} className={`${item.color} p-3 rounded-xl text-center border`}>
                <item.icon className="h-5 w-5 mx-auto mb-1" />
                <span className="text-xs font-medium">{item.label}</span>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Modules */}
        <div className="space-y-6">
          
          {/* Module 1: Introduction */}
          <ModuleCard 
            title="Introduction à la Numérologie" 
            icon={Compass}
            color="bg-gradient-to-r from-emerald-500 to-emerald-600"
            defaultOpen={true}
          >
            <div className="space-y-6">
              <div className="bg-emerald-50 p-5 rounded-xl border border-emerald-200">
                <h3 className="text-lg font-bold text-gray-800 mb-3">Qu'est-ce que la numérologie ?</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  La numérologie est un système de connaissance de soi qui utilise les nombres pour révéler des aspects de notre personnalité, 
                  nos talents, nos défis et notre chemin de vie. Basée sur les enseignements de Pythagore (570-495 av. J.-C.), 
                  elle considère que chaque nombre possède une vibration et une signification unique.
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                  <h4 className="font-semibold text-emerald-700 mb-2">Les nombres clés</h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• <strong>Chemin de vie</strong> : Mission de vie (date de naissance)</li>
                    <li>• <strong>Expression</strong> : Talents naturels (toutes les lettres)</li>
                    <li>• <strong>Intime</strong> : Désirs profonds (voyelles)</li>
                    <li>• <strong>Réalisation</strong> : Image publique (consonnes)</li>
                  </ul>
                </div>
                <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                  <h4 className="font-semibold text-emerald-700 mb-2">Conversion des lettres</h4>
                  <div className="text-sm text-gray-600">
                    <p className="mb-2">Système pythagoricien :</p>
                    <div className="grid grid-cols-3 gap-1 text-xs">
                      <span>A,J,S = 1</span>
                      <span>B,K,T = 2</span>
                      <span>C,L,U = 3</span>
                      <span>D,M,V = 4</span>
                      <span>E,N,W = 5</span>
                      <span>F,O,X = 6</span>
                      <span>G,P,Y = 7</span>
                      <span>H,Q,Z = 8</span>
                      <span>I,R = 9</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </ModuleCard>

          {/* Module 2: Signification des nombres 1-9 */}
          <ModuleCard 
            title="Les Nombres de 1 à 9" 
            icon={Hash}
            color="bg-gradient-to-r from-teal-500 to-teal-600"
          >
            <p className="text-gray-600 mb-6 text-sm">
              Chaque nombre de 1 à 9 possède une énergie et une signification unique. 
              Découvrez les forces et les défis associés à chaque vibration.
            </p>
            <div className="grid md:grid-cols-3 gap-4">
              {NUMBER_MEANINGS.map((num) => (
                <div key={num.number} className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                  <div className="flex items-center gap-3 mb-3">
                    <span className={`w-10 h-10 ${num.color} rounded-full flex items-center justify-center text-white font-bold text-lg`}>
                      {num.number}
                    </span>
                    <div>
                      <h4 className="font-bold text-gray-800">{num.name}</h4>
                      <p className="text-xs text-gray-500">{num.keywords}</p>
                    </div>
                  </div>
                  <div className="text-xs space-y-1">
                    <p className="text-emerald-600">✓ {num.traits}</p>
                    <p className="text-amber-600">⚠ {num.challenges}</p>
                  </div>
                </div>
              ))}
            </div>
          </ModuleCard>

          {/* Module 3: Nombres maîtres */}
          <ModuleCard 
            title="Les Nombres Maîtres (11, 22, 33)" 
            icon={Star}
            color="bg-gradient-to-r from-amber-500 to-amber-600"
          >
            <div className="bg-amber-50 p-4 rounded-xl border border-amber-200 mb-6">
              <p className="text-amber-700 text-sm">
                Les nombres maîtres ne sont pas réduits lors des calculs. Ils portent une vibration élevée 
                avec un potentiel exceptionnel mais aussi des défis plus intenses.
              </p>
            </div>
            <div className="space-y-4">
              {MASTER_NUMBERS.map((master) => (
                <div key={master.number} className="bg-gray-50 p-5 rounded-xl border border-amber-200">
                  <div className="flex items-center gap-4 mb-3">
                    <span className="w-14 h-14 bg-gradient-to-br from-amber-500 to-yellow-500 rounded-full flex items-center justify-center text-white font-bold text-xl">
                      {master.number}
                    </span>
                    <div>
                      <h4 className="font-bold text-gray-800 text-lg">{master.name}</h4>
                      <p className="text-gray-600 text-sm">{master.description}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-4">
                    {master.traits.map((trait, i) => (
                      <span key={i} className="text-xs bg-amber-100 text-amber-700 px-3 py-1.5 rounded-full text-center">
                        {trait}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </ModuleCard>

          {/* Module 4: Dettes karmiques */}
          <ModuleCard 
            title="Les Dettes Karmiques" 
            icon={AlertTriangle}
            color="bg-gradient-to-r from-rose-500 to-rose-600"
          >
            <div className="bg-rose-50 p-4 rounded-xl border border-rose-200 mb-6">
              <p className="text-rose-700 text-sm">
                Les dettes karmiques (13, 14, 16, 19) indiquent des leçons de vie spécifiques à apprendre. 
                Ce ne sont pas des malédictions mais des opportunités de croissance spirituelle.
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              {KARMIC_DEBTS.map((debt) => (
                <div key={debt.number} className="bg-gray-50 p-5 rounded-xl border border-rose-200">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="w-12 h-12 bg-rose-500 rounded-full flex items-center justify-center text-white font-bold">
                      {debt.number}
                    </span>
                    <h4 className="font-bold text-gray-800">{debt.lesson}</h4>
                  </div>
                  <p className="text-gray-600 text-sm mb-3">{debt.description}</p>
                  <p className="text-emerald-600 text-xs italic">💡 {debt.advice}</p>
                </div>
              ))}
            </div>
          </ModuleCard>

          {/* Module 5: Années personnelles */}
          <ModuleCard 
            title="Le Cycle des 9 Années" 
            icon={Calendar}
            color="bg-gradient-to-r from-cyan-500 to-cyan-600"
          >
            <div className="bg-cyan-50 p-4 rounded-xl border border-cyan-200 mb-6">
              <h4 className="font-semibold text-cyan-700 mb-2">Comment calculer votre année personnelle ?</h4>
              <p className="text-cyan-600 text-sm">
                Additionnez votre jour de naissance + mois de naissance + année en cours, puis réduisez à un chiffre.
                <br />
                <span className="text-cyan-700">Exemple : né le 15/03, en 2026 → 1+5+0+3+2+0+2+6 = 19 → 1+9 = 10 → 1+0 = 1 (Année 1)</span>
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              {PERSONAL_YEARS.map((py) => (
                <div key={py.year} className="bg-gray-50 p-4 rounded-xl border border-cyan-200">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="w-10 h-10 bg-cyan-500 rounded-full flex items-center justify-center text-white font-bold">
                      {py.year}
                    </span>
                    <h4 className="font-bold text-gray-800 text-sm">{py.theme}</h4>
                  </div>
                  <p className="text-gray-600 text-xs mb-2">{py.description}</p>
                  <p className="text-cyan-600 text-xs font-semibold">{py.action}</p>
                </div>
              ))}
            </div>
          </ModuleCard>

          {/* Module 6: Heures miroir */}
          <ModuleCard 
            title="Les Heures Miroir" 
            icon={Clock}
            color="bg-gradient-to-r from-indigo-500 to-indigo-600"
          >
            <p className="text-gray-600 mb-6 text-sm">
              Les heures miroir (11:11, 22:22...) et inversées (12:21, 13:31...) sont considérées comme des messages de l'univers. 
              Voici leurs significations principales :
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              {[
                { time: "11:11", meaning: "Éveil spirituel, alignement, pensées qui se manifestent" },
                { time: "12:12", meaning: "Foi en vos capacités, projets en bonne voie" },
                { time: "13:13", meaning: "Transformation positive, changements bénéfiques" },
                { time: "14:14", meaning: "Équilibre émotionnel, patience recommandée" },
                { time: "15:15", meaning: "Changements importants à venir, adaptabilité" },
                { time: "16:16", meaning: "Focus sur l'amour et la famille, harmonie" },
                { time: "17:17", meaning: "Chance et réussite, confiance en vous" },
                { time: "18:18", meaning: "Fin d'un cycle, accomplissement proche" },
                { time: "19:19", meaning: "Patience, vos efforts porteront leurs fruits" },
                { time: "20:20", meaning: "Intuition forte, écoutez votre voix intérieure" },
                { time: "21:21", meaning: "Succès et reconnaissance en approche" },
                { time: "22:22", meaning: "Maître bâtisseur, grands projets possibles" }
              ].map((mirror) => (
                <div key={mirror.time} className="bg-gray-50 p-3 rounded-xl border border-indigo-200 flex items-center gap-3">
                  <span className="text-indigo-600 font-mono font-bold text-lg">{mirror.time}</span>
                  <p className="text-gray-600 text-xs">{mirror.meaning}</p>
                </div>
              ))}
            </div>
          </ModuleCard>

        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-12 text-center"
        >
          <p className="text-gray-600 mb-6">
            Prêt à découvrir vos propres nombres ?
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Button
              data-testid="start-theme-btn"
              onClick={() => navigate('/numerologia/theme')}
              size="lg"
              className="rounded-full bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-400 hover:to-teal-500 text-white px-8 py-6 text-base font-semibold shadow-lg"
            >
              <Calculator className="mr-2 h-5 w-5" />
              Calculer mon thème
            </Button>
            <Button
              onClick={() => navigate('/numerologia/quiz')}
              size="lg"
              variant="outline"
              className="rounded-full border-emerald-300 text-emerald-700 hover:bg-emerald-50 px-8 py-6 text-base font-semibold"
            >
              <Brain className="mr-2 h-5 w-5" />
              Tester mes connaissances
            </Button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default NumerologiaKnowledgePage;
